// === Search Component ===

import store from '../state/store.js';
import { escapeHtml } from '../utils/dom.js';
import { SHORT_MONTHS } from '../utils/date.js';
import { getShiftedMonths } from '../services/filter-service.js';
import { pushScreen, back } from '../utils/nav.js';

const MAX_RESULTS = 30;

function normalizeHerd(input) {
  const val = (input || '').toLowerCase();
  if (val === 'store' || val === 'fattening') return 'beef';
  return val;
}

function matchesHerd(item) {
  if (!store.farmProfile.herdType) return true;
  const chosen = normalizeHerd(store.farmProfile.herdType);
  const relevant = (item.meta?.relevantHerdTypes || []).map(h => h.toLowerCase());
  return relevant.length === 0 || relevant.includes(chosen);
}

function searchableText(item) {
  const c = item.content || {};
  return [
    c.title,
    c.summary,
    c.notes,
    (c.tags || []).join(' '),
    (item.tasks || []).map(t => t.label).join(' '),
    item.meta?.category
  ].filter(Boolean).join(' ').toLowerCase();
}

function search(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return store.seed
    .filter(item => matchesHerd(item) && searchableText(item).includes(q))
    .slice(0, MAX_RESULTS);
}

function highlight(text, q) {
  if (!text) return '';
  const escaped = escapeHtml(text);
  if (!q) return escaped;
  const qEsc = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return escaped.replace(new RegExp(qEsc, 'gi'), m => `<mark>${m}</mark>`);
}

function formatMonths(item) {
  if (item.meta?.yearRound) return 'Year-round';
  const months = getShiftedMonths(item) || item.meta?.months || [];
  if (!months.length) return '';
  return months.map(m => SHORT_MONTHS[m - 1]).join(', ');
}

function renderResults(items, q) {
  if (!items.length) {
    return q
      ? `<div class="search-empty">No advisories match &ldquo;${escapeHtml(q)}&rdquo;</div>`
      : '';
  }
  return items.map(item => `
    <button class="search-result" data-id="${item.id}" role="option">
      <div class="search-result-title">${highlight(item.content?.title || '', q)}</div>
      <div class="search-result-summary">${highlight(item.content?.summary || '', q)}</div>
      <div class="search-result-meta">
        <span class="search-result-months">${formatMonths(item)}</span>
        ${item.meta?.urgency === 'Time-sensitive' ? '<span class="search-result-badge">Time-sensitive</span>' : ''}
      </div>
    </button>
  `).join('');
}

export function setupSearch({ onNavigate }) {
  const overlay = document.getElementById('searchOverlay');
  const input = document.getElementById('searchInput');
  const results = document.getElementById('searchResults');
  const hint = document.getElementById('searchHint');
  const btnOpen = document.getElementById('btnOpenSearch');
  const btnClose = document.getElementById('btnCloseSearch');

  if (!overlay || !btnOpen) return;

  function open() {
    overlay.removeAttribute('hidden');
    input.value = '';
    results.innerHTML = '';
    hint.style.display = '';
    document.body.classList.add('search-open');
    pushScreen(close);
    setTimeout(() => input.focus(), 30);
  }

  function close() {
    overlay.setAttribute('hidden', '');
    document.body.classList.remove('search-open');
  }

  function handleInput() {
    const q = input.value;
    if (!q.trim()) {
      results.innerHTML = '';
      hint.style.display = '';
      return;
    }
    hint.style.display = 'none';
    const matches = search(q);
    results.innerHTML = renderResults(matches, q);
  }

  btnOpen.addEventListener('click', open);
  btnClose.addEventListener('click', back);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) back();
  });

  input.addEventListener('input', handleInput);

  results.addEventListener('click', (e) => {
    const btn = e.target.closest('.search-result');
    if (!btn) return;
    const item = store.seed.find(i => i.id === btn.dataset.id);
    if (!item) return;
    back();
    onNavigate(item);
  });

  document.addEventListener('keydown', (e) => {
    const isOpen = !overlay.hasAttribute('hidden');
    if (e.key === 'Escape' && isOpen) {
      back();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      if (isOpen) back(); else open();
    }
  });
}
